import { config } from "@/config";
import { resolveOrganization } from "@/lib/utils";
import { AuthSession } from "@/types/auth";
import { AuthenticationFailedError } from "./AuthError";

const CLAIRE_SESSION_STORAGE_KEY = "claire_auth_session";
const LEGACY_TOKEN_STORAGE_KEY = "claire_bearer_token";

export type StartAuthPayload = {
  email: string;
  role: "user";
  organizationId?: string;
};

export type VerifyAuthPayload = {
  email: string;
  otp_code: string;
  role: "user";
};

type VerifyAuthResponse =
  | string
  | {
      token?: string;
      access_token?: string;
      accessToken?: string;
      refresh_token?: string;
      refreshToken?: string;
      role?: string;
      user?: AuthSession["user"];
      student?: AuthSession["student"];
    };

type RefreshAuthPayload = {
  refreshToken: string;
};

type RefreshAuthResponse = VerifyAuthResponse;

export function getStoredSession(): AuthSession | null {
  const storedSession = localStorage.getItem(CLAIRE_SESSION_STORAGE_KEY);

  if (!storedSession) {
    localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    return null;
  }

  try {
    const parsedSession = JSON.parse(storedSession) as Partial<AuthSession>;
    if (
      typeof parsedSession.accessToken !== "string" ||
      typeof parsedSession.refreshToken !== "string" ||
      !parsedSession.accessToken ||
      !parsedSession.refreshToken
    ) {
      clearStoredSession();
      return null;
    }

    return {
      ...parsedSession,
      accessToken: parsedSession.accessToken,
      refreshToken: parsedSession.refreshToken,
    };
  } catch {
    clearStoredSession();
    return null;
  }
}

export function storeSession(session: AuthSession): void {
  localStorage.setItem(CLAIRE_SESSION_STORAGE_KEY, JSON.stringify(session));
  localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
}

export function clearStoredSession(): void {
  localStorage.removeItem(CLAIRE_SESSION_STORAGE_KEY);
  localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
}

export function buildSigninUrl(): string {
  const organization = resolveOrganization();

  if (!organization) {
    return "/signin";
  }

  return `/signin?organization=${encodeURIComponent(organization)}`;
}

export async function startAuth(payload: StartAuthPayload): Promise<void> {
  await postAuth("/auth/start", payload);
}

export async function verifyAuth(payload: VerifyAuthPayload): Promise<AuthSession> {
  const response = await postAuth<VerifyAuthResponse>("/auth/verify", payload);
  return extractSession(response);
}

export async function refreshAuth(payload: RefreshAuthPayload): Promise<AuthSession> {
  const response = await postAuth<RefreshAuthResponse>("/auth/refresh", payload);
  return extractSession(response);
}

async function postAuth<T>(
  endpoint: string,
  payload: StartAuthPayload | VerifyAuthPayload | RefreshAuthPayload
): Promise<T> {
  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = `Authentication request failed with status ${response.status}`;

    try {
      const errorData = await response.json();
      message =
        errorData.detail ||
        errorData.message ||
        errorData.error ||
        message;
    } catch {
      // Ignore response parsing errors and keep the fallback message.
    }

    throw new AuthenticationFailedError(message);
  }

  if (response.status === 204) {
    return null as T;
  }

  return (await response.json()) as T;
}

function extractSession(payload: VerifyAuthResponse): AuthSession {
  if (typeof payload === "string") {
    throw new AuthenticationFailedError(
      "Authentication response did not include a refresh token"
    );
  }

  const accessToken =
    payload?.token || payload?.access_token || payload?.accessToken;
  const refreshToken = payload?.refresh_token || payload?.refreshToken;

  if (!accessToken || !refreshToken) {
    throw new AuthenticationFailedError(
      "Authentication response did not include both access and refresh tokens"
    );
  }

  return {
    accessToken,
    refreshToken,
    role: payload.role,
    user: payload.user,
    student: payload.student,
  };
}
